import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Stack, Tab, Tabs, Typography } from '@mui/material';

import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import LoadingPanel from 'src/pages/admin/components/LoadingPanel';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';

import { managedVaults } from '../vaults/helpers';
import { useVaults } from '../vaults/useVaults';
import { VaultSwitcher } from '../vaults/VaultSwitcher';
import { ActionDialog } from './ActionDialog';
import {
  RECONCILIATION_NS as NS,
  RECONCILIATION_PERMISSION_RESOURCE as RESOURCE,
  RECONCILIATION_TABS,
  type ReconciliationTab,
} from './constants';
import { ProposalDialog } from './ProposalDialog';
import { RunDialog } from './RunDialog';
import { ProposalsTable, ResolutionsTable } from './Tables';
import { useReconciliation } from './useReconciliation';
import { useReconciliationActions } from './useReconciliationActions';
import { useRunReconciliation } from './useRunReconciliation';

/**
 * XBIT-4864: the Operations queue — what operations clears from the console instead of SQL or a CLI. Round 1 has one tab,
 * Frozen vaults: pick a vault, then propose a share adjustment, approve it as a second operator (resolving the break) or
 * run reconciliation now. Later flows (UNRESOLVED work orders, ESCALATED close-outs: XBIT-4870) add tabs beside it.
 *
 * TODO(XBIT-4861, BFF round 2): the freeze list (engine !479) is not served yet, so the queue cannot list the frozen vaults
 * by itself; until then it is driven by the vault picker and the proposals / resolutions reads.
 */
const OperationsQueuePage = () => {
  const translate = useTranslate();
  const [tab, setTab] = useState<ReconciliationTab>(RECONCILIATION_TABS[0]);
  const { vaults, loading: vaultsLoading, loadError: vaultsError } = useVaults();
  const choices = useMemo(() => managedVaults(vaults), [vaults]);
  // The vault detail's frozen banner links here with ?vault=<id>.
  const [searchParams] = useSearchParams();
  const [vaultId, setVaultId] = useState<string | undefined>(
    searchParams.get('vault') ?? undefined
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const { proposals, resolutions, loading, loadError, refetch } = useReconciliation(vaultId);
  const actions = useReconciliationActions({ vaultId, onSettled: refetch });
  const run = useRunReconciliation(vaultId, refetch);

  useEffect(() => {
    if (!vaultId && choices.length > 0) setVaultId(choices[0].vaultId);
  }, [choices, vaultId]);

  const vaultName = choices.find((v) => v.vaultId === vaultId)?.name ?? vaultId ?? '';
  const opened = proposals.find((p) => p.id === openId) ?? null;

  return (
    <PermissionGuard resource={RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate(`${NS}.title`)}
          links={[
            { name: translate('resource.vault'), href: '#' },
            { name: translate(`${NS}.title`) },
          ]}
          action={
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:refresh-bold" />}
              onClick={refetch}
              disabled={loading || !vaultId}
            >
              {translate(`${NS}.refresh`)}
            </Button>
          }
        />

        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mt: 3 }}>
          {RECONCILIATION_TABS.map((value) => (
            <Tab key={value} value={value} label={translate(`${NS}.tabs.${value}`)} />
          ))}
        </Tabs>

        {tab === 'frozen' && (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Alert severity="info">{translate(`${NS}.freezeListPending`)}</Alert>
            {vaultsError && <Alert severity="error">{vaultsError}</Alert>}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <VaultSwitcher
                vaults={choices}
                value={vaultId ?? ''}
                onChange={(next) => {
                  setVaultId(next);
                  setOpenId(null);
                }}
                disabled={vaultsLoading}
              />
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <PermissionButton resource={RESOURCE} action="propose">
                  <Button
                    variant="contained"
                    disabled={!vaultId}
                    onClick={() => actions.open('propose')}
                  >
                    {translate(`${NS}.actions.propose`)}
                  </Button>
                </PermissionButton>
                <PermissionButton resource={RESOURCE} action="run">
                  <Button variant="outlined" disabled={!vaultId} onClick={run.open}>
                    {translate(`${NS}.actions.run`)}
                  </Button>
                </PermissionButton>
              </Stack>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <LoadingPanel isLoading={loading && proposals.length === 0 && resolutions.length === 0}>
              {vaultId && !loadError && (
                <Stack spacing={3}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {translate(`${NS}.proposals.title`)}
                    </Typography>
                    <ProposalsTable rows={proposals} onOpen={(row) => setOpenId(row.id)} />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1">
                      {translate(`${NS}.resolutions.title`)}
                    </Typography>
                    <ResolutionsTable rows={resolutions} />
                  </Stack>
                </Stack>
              )}
            </LoadingPanel>
          </Stack>
        )}

        <ProposalDialog proposal={opened} actions={actions} onClose={() => setOpenId(null)} />
        <ActionDialog form={actions} vaultName={vaultName} />
        <RunDialog form={run} vaultName={vaultName} />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default OperationsQueuePage;
